import { toast } from 'sonner';

/**
 * Keeps a long-lived browser tab from running an old build after a deploy.
 *
 * - Polls /version.json (never cached) and compares it with the version this
 *   bundle was built with (__APP_VERSION__). Checks run periodically, when the
 *   tab becomes visible again and when the network comes back.
 * - On a mismatch it shows a persistent "new version" toast with a Reload button.
 *   It never reloads by itself: the user may be in the middle of a form.
 * - After a deploy the old hashed chunk files are gone, so lazy-loaded pages of an
 *   old tab fail with a chunk error; that triggers one automatic reload.
 * - Every automatic reload is limited to once per server version (sessionStorage)
 *   so a misconfigured cache cannot cause a reload loop.
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_KEY = 'app_reload_for';

let latestVersion: string | null = null;
let updateAnnounced = false;

const isNewer = (serverVersion: string | null | undefined): serverVersion is string =>
    !!serverVersion && serverVersion !== __APP_VERSION__;

async function fetchServerVersion(): Promise<string | null> {
    try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return null;
        const data = await response.json();
        return typeof data?.version === 'string' ? data.version : null;
    } catch {
        return null;
    }
}

function reloadToLatest(version: string | null) {
    const url = new URL(window.location.href);
    if (version) url.searchParams.set('v', version);
    window.location.replace(url.toString());
}

function reloadOncePerVersion(version: string | null) {
    const key = version ?? 'unknown';
    try {
        if (sessionStorage.getItem(RELOAD_GUARD_KEY) === key) return false;
        sessionStorage.setItem(RELOAD_GUARD_KEY, key);
    } catch {
        // sessionStorage unavailable: fall through and reload once anyway
    }
    reloadToLatest(version);
    return true;
}

function announceUpdate(version: string) {
    latestVersion = version;
    if (updateAnnounced) return;
    updateAnnounced = true;

    toast.info('A new version is available', {
        id: 'app-update',
        description: 'Save your work, then reload to get the latest fixes.',
        duration: Infinity,
        action: { label: 'Reload', onClick: () => reloadToLatest(version) },
    });
}

async function checkForUpdate() {
    if (document.visibilityState === 'hidden') return;
    const serverVersion = await fetchServerVersion();
    if (isNewer(serverVersion)) announceUpdate(serverVersion);
}

function isChunkLoadError(reason: unknown): boolean {
    const message = String((reason as { message?: string })?.message ?? reason ?? '');
    return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \S+ failed/i.test(message);
}

export function startAppVersionWatcher() {
    if (__APP_VERSION__ === 'dev') return;

    // Vite emits this when a preloaded lazy chunk 404s (old chunk deleted by a deploy).
    window.addEventListener('vite:preloadError', (event) => {
        event.preventDefault();
        reloadOncePerVersion(latestVersion);
    });
    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason)) reloadOncePerVersion(latestVersion);
    });

    window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void checkForUpdate();
    });
    window.addEventListener('online', () => void checkForUpdate());
}
